/**
 * Mobile Navbar Item Component
 *
 * Single responsibility: Render a complete navbar menu item for mobile navbar
 * Follows Single Responsibility Principle
 */

import React from 'react';
import { NavbarIcon } from '../../shared';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
} from '@/design-system/utils';

export interface MobileNavbarItemProps {
  id: string;
  label: string;
  iconName: string;
  isActive?: boolean;
  onClick?: () => void;
  showArrow?: boolean;
}

export const MobileNavbarItem: React.FC<MobileNavbarItemProps> = ({
  id,
  label,
  iconName,
  isActive = false,
  onClick,
  showArrow = true,
}) => {
  return (
    <button
      key={id}
      onClick={onClick}
      style={{
        display: 'flex',
        padding: `${spacing[2.5]} ${spacing[1.5]}`, // 10px top/bottom, 6px left/right
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
        background: isActive
          ? backgroundColors.shade // #E7E5E4 - Active background
          : backgroundColors.primary, // #FFF - Default background
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        borderRadius: isActive ? borders.radius.sm : '0', // 4px border radius when active
      }}
      className={isActive ? '' : 'hover:bg-gray-50'}
    >
      <div
        style={{
          display: 'flex',
          gap: spacing[2], // 8px
          alignItems: 'center',
        }}
      >
        <div
          style={{
            color: isActive
              ? typographyColors.primary
              : typographyColors.tertiary,
          }}
        >
          <NavbarIcon iconName={iconName} size={20} alt={label} />
        </div>
        <span
          style={{
            color: typographyColors.primary, // var(--Colors-Typography-primary, #1C1917)
            textAlign: 'center',
            fontFamily: 'Gantari',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400, // Regular weight as specified
            lineHeight: '100%', // 100% line height as specified
            letterSpacing: '0%', // 0% letter spacing as specified
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>
      {showArrow && (
        <svg
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          style={{
            color: typographyColors.tertiary,
            flexShrink: 0,
          }}
        >
          <path
            d='M6 12L10 8L6 4'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
    </button>
  );
};
